import { createContext, useContext, useEffect, useReducer } from "react";

import { MainContext } from "../../../main/context/MainContextProvider";
import { SolarGridControlDataEventManager } from "../../dashboard/helpers/SolarGridControlDataEventManager";
import { solarGridControlDataReducer } from "../helpers/solarGridControlDataReducer";
import type { SolarGridControlData } from "../types";

type SolarGridControlContextType = {
    solarGridControlData: SolarGridControlData | null;
};

const SolarGridControlContext = createContext<SolarGridControlContextType | null>(null);

export function useSolarGridControlContext() {
    const context = useContext(SolarGridControlContext);
    if (!context) {
        throw new Error("useSolarGridControlContext deve ser usado dentro de seu Provider");
    }

    return context;
}

export function SolarGridControlContextProvider({ children }: { children: React.ReactNode }) {

    const { user, selectedDashboardUsina, setToastNotifications } = useContext(MainContext);

    const [solarGridControlData, dispatchSolarGridControlDataAction] =
        useReducer(solarGridControlDataReducer, null);

    const contextValue = {
        solarGridControlData,
    };

    useEffect(() => {
        dispatchSolarGridControlDataAction({ type: 'RESET_CONNECTION' });

        if (!user || !selectedDashboardUsina?.usinaIds.id) {
            console.debug("SolarGridControlDataEventManager sem usuário ou usina selecionada, não iniciando conexão de Tempo Real");
            return;
        }

        const userId = user.userId;
        const usinaId = selectedDashboardUsina.usinaIds.id;

        const solarGridControlDataEvtManager = new SolarGridControlDataEventManager(
            userId,
            usinaId
        );

        solarGridControlDataEvtManager.on("connected", () => {
            console.debug("Conectado Tempo Real solarGridControlData");
        });
        solarGridControlDataEvtManager.on("disconnected", (reason) => {
            console.debug("Desconectado Tempo Real solarGridControlData:", { reason });
        });
        solarGridControlDataEvtManager.on("reconnecting", (reason) => {
            console.debug("Reconectando Tempo Real solarGridControlData:", { reason });
        });
        solarGridControlDataEvtManager.on("error", (errorMessage) => {
            console.debug("Erro Tempo Real solarGridControlData:", errorMessage);
            if (errorMessage.includes("Número máximo de conexões simultâneas")) {
                setToastNotifications((toastNotifications) => ({
                    isAdding: true,
                    notifications: [
                        ...toastNotifications.notifications,
                        {
                            severity: "warning" as const,
                            text: "Número máximo de conexões simultâneas do cliente atingido.",
                        },
                    ],
                }))
            }
        });

        solarGridControlDataEvtManager.on("solarGridControlCardUpdate", (solarGridControlCardUpdate) => {
            // console.debug("Atualização Tempo Real Controle de rede solar:", solarGridControlCardUpdate);
            dispatchSolarGridControlDataAction({
                type: 'SOLAR_GRID_CONTROL_CARD_UPDATE',
                payload: solarGridControlCardUpdate,
            });
        });

        solarGridControlDataEvtManager.on("ropCardUpdate", (ropCardUpdate) => {
            // console.debug("SolarGridControl RopCardUpdate recebido:", ropCardUpdate);
            dispatchSolarGridControlDataAction({
                type: 'ROP_CARD_UPDATE',
                payload: ropCardUpdate,
            });
        });

        return () => {
            solarGridControlDataEvtManager.destroy();
        };
    }, [user, selectedDashboardUsina]);

    return (
        <SolarGridControlContext.Provider value={contextValue}>
            {children}
        </SolarGridControlContext.Provider>
    );
}